import React from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';

import useFilestackPicker from '../../hooks/useFilestackPicker';

const axiosClient = axios.create({
	withCredentials: true,
});

const ACCEPTED_FILE_TYPES = [
	'audio/*',
	'image/*',
	'video/*',
	'text/csv',
	'text/plain',
	'.pdf',
	'.doc',
	'.docx',
	'.ppt',
	'.pptx',
	'.xls',
	'.xlsx',
	'.odp',
	'.ods',
	'.odt',
	'.ics',
	// TODO: Archives, zip, tar, etc.
];

const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500 MB

const propTypes = {

	/**
	 * Unique id for targetting the filestack picker, if any.
	 * A default will be created if not provided.
	 */
	id: PropTypes.string,

	/**
	 * The type of files to allow.
	 */
	accept: PropTypes.arrayOf(PropTypes.string).isRequired,

	/**
	 * The maximum number of files to upload.
	 */
	maxFiles: PropTypes.number.isRequired,

	/**
	 * The visibility settings of the files to upload.
	 */
	visibility: PropTypes.oneOf(['public', 'private']).isRequired,

	/**
	 * A flag for returning all associated file data, rather than just the url.
	 */
	fullFileData: PropTypes.bool.isRequired,

	/**
	 * The settings for filestack.
	 */
	filestack: PropTypes.shape({

		// The API key
		apiKey: PropTypes.string.isRequired,

		// The security policy and signature
		security: PropTypes.shape({

			policy: PropTypes.string,

			signature: PropTypes.string,

		}),

		// Flag indicating if mode is production.
		production: PropTypes.bool,

		// The path
		path: PropTypes.string,

		// The container
		container: PropTypes.string,

		// The region
		region: PropTypes.string,

	}).isRequired,

	/**
	 * The content to render to initiate the file upload, i.e. a button.
	 */
	children: PropTypes.element.isRequired,

	/**
	 * Callback function to handle errors during file uploads.
	 */
	onError: PropTypes.func.isRequired,

	/**
	 * Callback function to handle successful file uploads.
	 */
	onUpload: PropTypes.func.isRequired,

};

const defaultProps = {
	accept: ACCEPTED_FILE_TYPES,
	maxFiles: 1,
	visibility: 'private',
	fullFileData: false,
};

/**
 * Shared component to render a file upload handler using filestack.
 * Saves successful uploads to the TC Media Library.
 *
 * @param   {Object} props  The props.
 * @returns {Object}        The rendered component.
 */
const FileUpload = (props) => {

	const {
		id,
		accept,
		maxFiles,
		fullFileData,
		visibility,
		filestack,
		children,
		onError,
		onUpload,
	} = props;

	const pickerOptions = {
		accept,
		maxFiles,
		maxSize: MAX_FILE_SIZE,
		imageMax: [2000, 2000], // only applies to images
		transformations: { // only applies to images
			crop: true,
		},
	};

	const handleSuccess = (response) => {

		const uploaded = response.filesUploaded;

		const data = [];

		// Loop through the filestack files and convert to format required by media library
		for (let i = 0; i < uploaded.length; i++) {

			data.push({
				...uploaded[i],
				visibility,
			});

		}

		// Save the files to the media library
		axiosClient.post('/api/v2/media', { data }).then((res) => {

			if (res && res.data) {

				// Convert the response to a valid url for each file
				const fileUrls = res.data.map((file) => {

					return {
						url: file.url,
						formattedUrl: `${file.url}?policy=${file.policy}&signature=${file.signature}`
					};

				});

				if (fullFileData) {

					const formattedURLMap = {};

					fileUrls.forEach((result) => { formattedURLMap[result.url] = result.formattedUrl; });

					onUpload(data.map((i) => ({ ...i, url: formattedURLMap[i.url] })));

				} else {

					onUpload(fileUrls.map((file) => file.formattedUrl));

				}

			}

		}).catch(onError);

	};

	const { pickerId, togglePicker } = useFilestackPicker({
		id,
		filestack,
		options: pickerOptions,
		onSuccess: handleSuccess,
		onError
	});

	if (!pickerId) { return null; }

	return (
		<div className="tc-file-upload-container">
			{React.cloneElement(children, { onClick: () => togglePicker() })}
			<div id={pickerId} />
		</div>
	);
};

FileUpload.propTypes = propTypes;
FileUpload.defaultProps = defaultProps;

export default FileUpload;
